import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import CountUp from "react-countup";
import { useAuth } from "@/context/AuthContext";
import api, { formatCurrency, COUNTRY_FLAGS, formatApiErrorDetail } from "@/lib/api";
import {
  CreditCard, Globe, DollarSign, ShieldCheck, FileImage, Phone,
  Wallet, Send, CheckCircle2, Loader2, ArrowRight, ArrowLeft, Lock, AlertCircle, Sparkles
} from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "@/components/ImageUploader";
import { PAYOUT_FIELDS, isPayoutDestinationComplete, formatPayoutDestination } from "@/lib/payoutFields";

const STEPS = [
  { id: 1, label: "Brand", Icon: CreditCard },
  { id: 2, label: "Country", Icon: Globe },
  { id: 3, label: "Value", Icon: DollarSign },
  { id: 4, label: "Quote", Icon: Sparkles },
  { id: 5, label: "Card Details", Icon: ShieldCheck },
  { id: 6, label: "Payout", Icon: Wallet },
  { id: 7, label: "Submit", Icon: Send },
];

export default function Sell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [brands, setBrands] = useState([]);
  const [countries, setCountries] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [search, setSearch] = useState("");
  const [quote, setQuote] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittedTx, setSubmittedTx] = useState(null);

  const [form, setForm] = useState({
    brand_id: "",
    country: "US",
    amount: 100,
    payout_method: "paypal",
    payout_destination: {},
    card_details: {
      condition: "new",
      format: "egift",
      card_number: "",
      pin: "",
      expiration: "",
      images: [],
      phone: "",
      receipt_available: null,
      purchase_method: null,
      activation_status: "activated",
      remaining_balance: null,
      region_restriction: "global",
      delivery_method: "email",
      date_purchased: "",
      store_of_purchase: "",
    },
  });

  useEffect(() => {
    api.get("/catalog/brands").then((r) => setBrands(r.data));
    api.get("/catalog/countries").then((r) => setCountries(r.data));
  }, []);

  useEffect(() => {
    api.get(`/catalog/payout-methods?country=${form.country}`).then((r) => setPayouts(r.data));
  }, [form.country]);

  // auto-quote on relevant changes
  useEffect(() => {
    if (!form.brand_id || !form.amount || form.amount <= 0) return;
    const t = setTimeout(async () => {
      setLoadingQuote(true);
      try {
        const { data } = await api.post("/rates/quote", {
          brand_id: form.brand_id, country: form.country, amount: Number(form.amount),
        });
        setQuote(data);
      } catch {
        setQuote(null);
      } finally {
        setLoadingQuote(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [form.brand_id, form.country, form.amount]);

  // Persist draft in localStorage
  useEffect(() => {
    const saved = localStorage.getItem("giftopay_sell_draft");
    if (saved) {
      try {
        const d = JSON.parse(saved);
        setForm((f) => ({ ...f, ...d, card_details: { ...f.card_details, ...(d.card_details || {}) } }));
      } catch {/* ignore */}
    }
  }, []);

  // Pre-fill from URL query params (e.g., /sell?brand=amazon&country=US&amount=100)
  // Runs after the draft load so landing-page links always win.
  useEffect(() => {
    const brandParam = searchParams.get("brand");
    const countryParam = searchParams.get("country");
    const amountParam = searchParams.get("amount");
    if (!brandParam && !countryParam && !amountParam) return;
    setForm((f) => ({
      ...f,
      brand_id: brandParam || f.brand_id,
      country: countryParam || f.country,
      amount: amountParam ? Number(amountParam) : f.amount,
    }));
    // Advance the wizard, but never past the amount step unless the URL
    // already supplied a concrete amount — users must enter their card value.
    if (amountParam && brandParam && countryParam) {
      setStep((s) => Math.max(s, 4)); // skip straight to quote
    } else if (brandParam && countryParam) {
      setStep((s) => Math.max(s, 3)); // ask for amount
    } else if (brandParam) {
      setStep((s) => Math.max(s, 2)); // ask for country
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    localStorage.setItem("giftopay_sell_draft", JSON.stringify(form));
  }, [form]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setCard = (k, v) => setForm((f) => ({ ...f, card_details: { ...f.card_details, [k]: v } }));

  const filteredBrands = useMemo(
    () => brands.filter((b) => b.name.toLowerCase().includes(search.toLowerCase())),
    [brands, search]
  );

  const next = () => setStep((s) => Math.min(7, s + 1));
  const prev = () => setStep((s) => Math.max(1, s - 1));

  // Smoothly scrolls the element matching the selector into view. Used after
  // discrete option selection (radios / payout methods) so the user sees the
  // next input they need to interact with without having to hunt.
  const scrollTo = (selector, block = "center") => {
    if (typeof window === "undefined") return;
    requestAnimationFrame(() => {
      const el = document.querySelector(selector);
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block });
      }
    });
  };

  const submit = async () => {
    if (!user) {
      toast.error("Please sign in to submit a transaction");
      navigate("/login", { state: { from: "/sell" } });
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/transactions/submit", {
        brand_id: form.brand_id,
        country: form.country,
        amount: Number(form.amount),
        payout_method: form.payout_method,
        payout_destination: form.payout_destination,
        card_details: form.card_details,
      });
      setSubmittedTx(data);
      localStorage.removeItem("giftopay_sell_draft");
      toast.success("Transaction submitted!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const canProceed = () => {
    if (step === 1) return !!form.brand_id;
    if (step === 2) return !!form.country;
    if (step === 3) return form.amount > 0;
    if (step === 4) return !!quote;
    if (step === 5) {
      const cd = form.card_details;
      return cd.condition && cd.format && cd.card_number && cd.card_number.length >= 8;
    }
    if (step === 6) return !!form.payout_method && isPayoutDestinationComplete(form.payout_method, form.payout_destination);
    return true;
  };

  if (submittedTx) return <SubmittedView tx={submittedTx} onView={() => navigate(`/transactions/${submittedTx.tx_id}`)} />;

  const selectedBrand = brands.find((b) => b.id === form.brand_id);
  const selectedCountry = countries.find((c) => c.code === form.country);

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-10" data-testid="sell-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#0B3C5D] tracking-tight">Sell your gift card</h1>
          <p className="mt-2 text-sm text-slate-600">Secure submission with 256-bit encryption. Takes ~ 2 minutes.</p>
        </div>

        {/* Progress */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-6 mb-6">
          <div className="hidden sm:flex items-center justify-between gap-2">
            {STEPS.map(({ id, label, Icon }, i) => (
              <React.Fragment key={id}>
                <div className={`flex items-center gap-2 ${step === id ? "text-[#0B3C5D]" : step > id ? "text-[#00C853]" : "text-slate-400"}`}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 ${
                    step === id ? "border-[#0B3C5D] bg-[#0B3C5D] text-white" :
                    step > id ? "border-[#00C853] bg-[#00C853] text-white" :
                    "border-slate-200 bg-white"
                  }`}>
                    {step > id ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  </div>
                  <span className="text-xs font-semibold hidden lg:inline">{label}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 ${step > id ? "bg-[#00C853]" : "bg-slate-200"}`} />
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="sm:hidden flex items-center justify-between text-xs">
            <span className="font-semibold text-[#0B3C5D]">Step {step} / 7</span>
            <span className="text-slate-500">{STEPS[step - 1].label}</span>
          </div>
          <div className="sm:hidden mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-[#00C853] transition-all" style={{ width: `${(step / 7) * 100}%` }} />
          </div>
        </div>

        {/* Step content */}
        <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-[0_8px_32px_rgba(11,60,93,0.06)]">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
            >
              {step === 1 && (
                <Step title="Pick a brand" subtitle="Search and select the gift card you want to sell.">
                  <input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search brand..."
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#0B3C5D] focus:ring-4 focus:ring-[#0B3C5D]/10"
                    data-testid="sell-brand-search"
                  />
                  <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-[420px] overflow-y-auto pr-1">
                    {filteredBrands.map((b) => (
                      <button
                        key={b.id}
                        onClick={() => setField("brand_id", b.id)}
                        className={`text-left rounded-2xl border-2 p-3 transition ${
                          form.brand_id === b.id ? "border-[#0B3C5D] bg-[#0B3C5D]/5" : "border-slate-200 hover:border-[#0B3C5D]/40"
                        }`}
                        data-testid={`sell-brand-${b.id}`}
                      >
                        <p className="font-display font-bold text-sm text-[#0B3C5D]">{b.name}</p>
                        <p className="text-[11px] text-slate-500">{b.category}</p>
                        <p className="mt-1 text-[11px] font-semibold text-[#00C853]">
                          {Math.round((b.rate.US || 0) * 100)}% US rate
                        </p>
                      </button>
                    ))}
                  </div>
                </Step>
              )}

              {step === 2 && (
                <Step title="Where are you selling from?" subtitle="Choose your country to set currency and payout options.">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {countries.map((c) => (
                      <button
                        key={c.code}
                        onClick={() => setField("country", c.code)}
                        className={`rounded-2xl border-2 p-5 text-center transition ${
                          form.country === c.code ? "border-[#0B3C5D] bg-[#0B3C5D]/5" : "border-slate-200 hover:border-[#0B3C5D]/40"
                        }`}
                        data-testid={`sell-country-${c.code}`}
                      >
                        <div className="text-3xl">{COUNTRY_FLAGS[c.code]}</div>
                        <p className="mt-2 font-display font-bold text-sm text-[#0B3C5D]">{c.name}</p>
                        <p className="text-[11px] text-slate-500">{c.currency}</p>
                      </button>
                    ))}
                  </div>
                </Step>
              )}

              {step === 3 && (
                <Step title="Card value" subtitle={`In ${selectedCountry?.currency || "USD"}. Most cards: 25 – 1000.`}>
                  <div className="relative max-w-md">
                    <input
                      type="number"
                      min="10"
                      step="5"
                      value={form.amount}
                      onChange={(e) => setField("amount", Number(e.target.value))}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-5 text-3xl font-display font-bold text-[#0B3C5D] focus:outline-none focus:border-[#0B3C5D] focus:ring-4 focus:ring-[#0B3C5D]/10"
                      data-testid="sell-amount"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium text-slate-400">{selectedCountry?.currency || "USD"}</span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {[25, 50, 100, 250, 500, 1000].map((v) => (
                      <button key={v} onClick={() => setField("amount", v)}
                        className="px-3 py-1.5 rounded-full text-sm font-medium border border-slate-200 text-slate-700 hover:border-[#0B3C5D] hover:text-[#0B3C5D]"
                        data-testid={`sell-amount-preset-${v}`}>
                        {v}
                      </button>
                    ))}
                  </div>
                </Step>
              )}

              {step === 4 && (
                <Step title="Your guaranteed payout" subtitle="Rate locked for 5 minutes once you submit.">
                  <div className="rounded-3xl bg-navy-gradient text-white p-8 max-w-lg">
                    <p className="text-xs uppercase tracking-[0.2em] text-white/70">Final Payout</p>
                    <p className="mt-2 font-display text-5xl font-bold tracking-tight">
                      {quote ? (
                        <>
                          <span className="text-[#00C853]">{quote.currency === "USD" ? "$" : quote.currency === "GBP" ? "£" : quote.currency === "CAD" ? "C$" : "د.إ"}</span>
                          <CountUp end={quote.final_payout} duration={0.6} decimals={2} separator="," preserveValue />
                        </>
                      ) : loadingQuote ? "..." : "—"}
                    </p>
                    <div className="mt-6 space-y-2.5 text-sm">
                      <Row label={`${selectedBrand?.name} (${selectedCountry?.name})`} value={quote ? `${(quote.rate_pct * 100).toFixed(1)}%` : "—"} />
                      <Row label="Gross" value={quote ? formatCurrency(quote.gross_payout, quote.currency) : "—"} />
                      <Row label="Processing fee" value={quote ? `- ${formatCurrency(quote.processing_fee, quote.currency)}` : "—"} />
                    </div>
                    <p className="mt-6 inline-flex items-center gap-1.5 text-xs text-white/70">
                      <Lock className="w-3.5 h-3.5 text-[#00C853]" /> Rate guaranteed for 5 minutes
                    </p>
                  </div>
                </Step>
              )}

              {step === 5 && (
                <Step
                  title="Card details"
                  subtitle="All data is encrypted with 256-bit SSL. We never store unmasked card numbers."
                >
                  <SecurityBadges />

                  <div className="grid sm:grid-cols-2 gap-5 mt-6">
                    <RadioGroup
                      label="Card Condition *"
                      value={form.card_details.condition}
                      onChange={(v) => { setCard("condition", v); scrollTo('[data-testid="card-number"]'); }}
                      options={[{ v: "new", l: "New (Never Used)" }, { v: "used", l: "Used (Partial Balance)" }]}
                      testid="card-condition"
                    />
                    <RadioGroup
                      label="Card Format *"
                      value={form.card_details.format}
                      onChange={(v) => { setCard("format", v); scrollTo('[data-testid="card-number"]'); }}
                      options={[{ v: "egift", l: "E-Gift (Digital)" }, { v: "physical", l: "Physical Card" }]}
                      testid="card-format"
                    />
                  </div>

                  <div className="mt-5 grid sm:grid-cols-2 gap-5">
                    <TextField
                      label="Card Number *"
                      helper="Enter exactly as shown on the card."
                      value={form.card_details.card_number}
                      onChange={(v) => setCard("card_number", v)}
                      testid="card-number"
                      masked
                    />
                    <TextField
                      label="PIN / CVV"
                      helper="Enter the PIN or CVV if your card has one."
                      value={form.card_details.pin}
                      onChange={(v) => setCard("pin", v)}
                      testid="card-pin"
                    />
                  </div>

                  <div className="mt-5 grid sm:grid-cols-2 gap-5">
                    <TextField
                      label="Expiration Date"
                      placeholder="MM/YY"
                      value={form.card_details.expiration}
                      onChange={(v) => setCard("expiration", v)}
                      testid="card-exp"
                    />
                    <TextField
                      label="Phone Number"
                      placeholder="+1 555 ..."
                      value={form.card_details.phone}
                      onChange={(v) => setCard("phone", v)}
                      testid="card-phone"
                    />
                  </div>

                  {form.card_details.condition === "used" && (
                    <div className="mt-5">
                      <TextField
                        label="Remaining Balance"
                        type="number"
                        value={form.card_details.remaining_balance ?? ""}
                        onChange={(v) => setCard("remaining_balance", v === "" ? null : Number(v))}
                        testid="card-balance"
                      />
                    </div>
                  )}

                  <div className="mt-6 rounded-2xl border border-slate-200 p-5 bg-slate-50/60" data-testid="card-additional-details">
                    <p className="font-display font-semibold text-[#0B3C5D]">
                      Additional card details
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      These help our team verify your card faster. All fields are optional but recommended.
                    </p>
                    <div className="mt-4 grid sm:grid-cols-2 gap-5">
                      <SelectField label="Receipt Available?" value={form.card_details.receipt_available} onChange={(v) => setCard("receipt_available", v === "" ? null : v === "true")} options={[{ v: "", l: "—" }, { v: "true", l: "Yes" }, { v: "false", l: "No" }]} testid="card-receipt" />
                      <SelectField label="Purchase Method" value={form.card_details.purchase_method || ""} onChange={(v) => setCard("purchase_method", v || null)} options={[{ v: "", l: "—" }, { v: "personal", l: "Bought personally" }, { v: "gift", l: "Received as gift" }, { v: "reseller", l: "Reseller purchase" }]} testid="card-purchase-method" />
                      <SelectField label="Activation Status" value={form.card_details.activation_status} onChange={(v) => setCard("activation_status", v)} options={[{ v: "activated", l: "Activated" }, { v: "unsure", l: "Not sure" }]} testid="card-activation" />
                      <SelectField label="Region Restriction" value={form.card_details.region_restriction} onChange={(v) => setCard("region_restriction", v)} options={[{ v: "global", l: "Global" }, { v: "us_only", l: "US only" }, { v: "region_specific", l: "Region-specific" }]} testid="card-region" />
                      <SelectField label="Delivery Method" value={form.card_details.delivery_method} onChange={(v) => setCard("delivery_method", v)} options={[{ v: "email", l: "Email delivery" }, { v: "physical_pickup", l: "Physical pickup" }, { v: "in_store", l: "In-store purchase" }]} testid="card-delivery" />
                      <TextField label="Date Purchased" type="date" value={form.card_details.date_purchased} onChange={(v) => setCard("date_purchased", v)} testid="card-date" />
                      <TextField label="Store of Purchase" value={form.card_details.store_of_purchase} onChange={(v) => setCard("store_of_purchase", v)} testid="card-store" />
                    </div>
                  </div>

                  <div className="mt-6 rounded-2xl border-2 border-dashed border-slate-300 p-6 text-center text-sm text-slate-500" data-testid="card-image-upload">
                    <ImageUploader
                      images={form.card_details.images}
                      onChange={(ids) => setCard("images", ids)}
                    />
                  </div>
                </Step>
              )}

              {step === 6 && (
                <Step title="How should we pay you?" subtitle="Choose a method available in your country.">
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {payouts.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => {
                          setField("payout_method", p.id);
                          // Reset destination on method change so we don't ship stale fields
                          setField("payout_destination", {});
                          // Scroll to the payout-details section so the user sees the fields to fill
                          scrollTo('[data-testid="payout-details-section"]', "start");
                        }}
                        className={`text-left rounded-2xl border-2 p-4 transition ${
                          form.payout_method === p.id ? "border-[#0B3C5D] bg-[#0B3C5D]/5" : "border-slate-200 hover:border-[#0B3C5D]/40"
                        }`}
                        data-testid={`payout-${p.id}`}
                      >
                        <p className="font-display font-bold text-sm text-[#0B3C5D]">{p.name}</p>
                        <p className="text-[11px] text-slate-500 mt-0.5">ETA: {p.eta}</p>
                        {p.fee_pct > 0 && <p className="text-[11px] text-slate-500">Fee: {(p.fee_pct * 100).toFixed(1)}%</p>}
                      </button>
                    ))}
                  </div>

                  <div className="mt-7" data-testid="payout-details-section">
                    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">
                      {payouts.find((p) => p.id === form.payout_method)?.name || "Payout"} details
                    </p>
                    <div className="grid sm:grid-cols-2 gap-4 max-w-2xl">
                      {(PAYOUT_FIELDS[form.payout_method] || []).map((f) => (
                        <TextField
                          key={f.key}
                          label={f.label + (f.required ? " *" : "")}
                          placeholder={f.placeholder}
                          value={form.payout_destination?.[f.key] || ""}
                          onChange={(v) => setField("payout_destination", { ...(form.payout_destination || {}), [f.key]: v })}
                          testid={`payout-field-${f.key}`}
                        />
                      ))}
                    </div>
                    <p className="mt-3 text-xs text-slate-500">
                      Double-check these — payouts go directly to the destination you provide. We can&apos;t reverse a payout sent to the wrong address.
                    </p>
                  </div>
                </Step>
              )}

              {step === 7 && (
                <Step title="Review & submit" subtitle="Confirm your details — we’ll start processing immediately.">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <ReviewRow label="Brand" value={selectedBrand?.name} />
                    <ReviewRow label="Country" value={`${COUNTRY_FLAGS[form.country]} ${selectedCountry?.name}`} />
                    <ReviewRow label="Card Value" value={formatCurrency(form.amount, selectedCountry?.currency || "USD")} />
                    <ReviewRow label="Card Condition" value={form.card_details.condition === "new" ? "New" : "Used"} />
                    <ReviewRow label="Card Format" value={form.card_details.format === "egift" ? "E-Gift" : "Physical"} />
                    <ReviewRow label="Payout Method" value={payouts.find((p) => p.id === form.payout_method)?.name} />
                  </div>

                  <div className="mt-6 rounded-2xl bg-[#00C853]/8 border border-[#00C853]/30 p-5">
                    <div className="flex items-center justify-between">
                      <p className="text-xs uppercase tracking-wider text-slate-600">You will receive</p>
                      <span className="text-[11px] text-[#0B3C5D] font-medium flex items-center gap-1"><Lock className="w-3 h-3"/> 5 min lock</span>
                    </div>
                    <p className="font-display text-4xl font-bold text-[#0B3C5D] mt-1">
                      {quote ? formatCurrency(quote.final_payout, quote.currency) : "—"}
                    </p>
                  </div>

                  <div className="mt-6 flex items-start gap-3 text-sm text-slate-600">
                    <AlertCircle className="w-5 h-5 text-amber-500 shrink-0" />
                    <p>
                      By submitting you confirm you legally own this card. False submissions may
                      result in a permanent account ban.
                    </p>
                  </div>
                </Step>
              )}
            </motion.div>
          </AnimatePresence>

          <div className="mt-8 flex items-center justify-between">
            <button
              onClick={prev}
              disabled={step === 1}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-slate-200 text-slate-700 text-sm font-medium disabled:opacity-40"
              data-testid="sell-prev"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>

            {step < 7 ? (
              <button
                onClick={next}
                disabled={!canProceed()}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#0B3C5D] text-white text-sm font-semibold btn-primary disabled:opacity-40"
                data-testid="sell-next"
              >
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={submit}
                disabled={submitting || !user}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#00C853] text-white text-sm font-semibold btn-accent disabled:opacity-50"
                data-testid="sell-submit"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Submit transaction
              </button>
            )}
          </div>
          {!user && step === 7 && (
            <p className="mt-3 text-xs text-amber-600 text-right">You need to <a href="/login" className="underline">sign in</a> to submit.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function Step({ title, subtitle, children }) {
  return (
    <div>
      <h2 className="font-display text-2xl font-bold text-[#0B3C5D] tracking-tight">{title}</h2>
      <p className="mt-1 text-sm text-slate-600">{subtitle}</p>
      <div className="mt-6">{children}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-white/80">
      <span>{label}</span>
      <span className="font-medium text-white">{value}</span>
    </div>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-200 px-4 py-3">
      <p className="text-[11px] uppercase tracking-wider text-slate-500">{label}</p>
      <p className="mt-0.5 text-sm font-semibold text-[#0B3C5D]">{value || "—"}</p>
    </div>
  );
}

function TextField({ label, helper, value, onChange, testid, masked, type = "text", placeholder }) {
  return (
    <div>
      <label className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</label>
      <input
        type={masked ? "text" : type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#0B3C5D] focus:ring-4 focus:ring-[#0B3C5D]/10"
        data-testid={testid}
        autoComplete="off"
      />
      {helper && <p className="mt-1 text-[11px] text-slate-500">{helper}</p>}
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</label>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#0B3C5D] focus:ring-4 focus:ring-[#0B3C5D]/10"
        data-testid={testid}
      >
        {options.map((o) => (
          <option key={String(o.v)} value={o.v}>{o.l}</option>
        ))}
      </select>
    </div>
  );
}

function RadioGroup({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</label>
      <div className="mt-1.5 grid grid-cols-2 gap-2">
        {options.map((o) => (
          <button
            key={o.v}
            type="button"
            onClick={() => onChange(o.v)}
            className={`rounded-xl border-2 p-3 text-sm font-medium transition ${
              value === o.v ? "border-[#0B3C5D] bg-[#0B3C5D]/5 text-[#0B3C5D]" : "border-slate-200 text-slate-700 hover:border-[#0B3C5D]/40"
            }`}
            data-testid={`${testid}-${o.v}`}
          >
            {o.l}
          </button>
        ))}
      </div>
    </div>
  );
}

function SecurityBadges() {
  return (
    <div className="grid grid-cols-3 gap-3">
      {[
        { Icon: Lock, t: "256-bit SSL" },
        { Icon: ShieldCheck, t: "Fraud Protected" },
        { Icon: CheckCircle2, t: "Encrypted" },
      ].map(({ Icon, t }, i) => (
        <div key={i} className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2 flex items-center gap-2 text-xs text-slate-700">
          <Icon className="w-4 h-4 text-[#00C853]" /> {t}
        </div>
      ))}
    </div>
  );
}

function SubmittedView({ tx, onView }) {
  return (
    <div className="min-h-screen bg-[#F8FAFC] py-16 flex items-center justify-center px-4" data-testid="submitted-view">
      <div className="max-w-xl w-full bg-white rounded-3xl border border-slate-200 shadow-xl p-8 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-[#00C853]/10 flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-[#00C853]" strokeWidth={2.5} />
        </div>
        <h2 className="font-display text-2xl font-bold text-[#0B3C5D] mt-5">Submission received!</h2>
        <p className="mt-2 text-sm text-slate-600">
          Your transaction <span className="font-semibold text-[#0B3C5D]">{tx.tx_id}</span> is in queue. We&apos;ll move it through verifying → payment processing in minutes.
        </p>
        <div className="mt-6 rounded-2xl bg-slate-50 border border-slate-200 p-5 text-left text-sm">
          <p className="text-xs uppercase tracking-wider text-slate-500">Estimated payout</p>
          <p className="font-display text-2xl font-bold text-[#0B3C5D] mt-1">{formatCurrency(tx.final_payout, tx.currency)}</p>
          <p className="mt-2 text-xs text-slate-500">Status: <span className="font-semibold text-amber-600 capitalize">{tx.status}</span></p>
        </div>
        <button
          onClick={onView}
          className="mt-6 inline-flex items-center justify-center w-full rounded-full bg-[#0B3C5D] text-white py-3.5 font-semibold btn-primary"
          data-testid="submitted-track-btn"
        >
          Track Transaction <ArrowRight className="ml-2 w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
